/**
 * Nauma — Media grouping by colour variant.
 *
 * Dawn natively supports only ONE image swap per variant. This script layers
 * on top of that to show a FULL SET of images per colour, using the colour
 * name already present in each image's alt text (tagged server-side in
 * snippets/product-media-gallery.liquid and snippets/product-media-modal.liquid
 * via data-variant-color).
 *
 * Images whose alt text doesn't match a colour option value are tagged
 * data-variant-color="all" and always stay visible (e.g. size chart).
 *
 * Filters both the main gallery/thumbnails AND the zoom/lightbox modal.
 *
 * Requires: constants.js and pubsub.js loaded earlier (Dawn core assets).
 */
(function () {
  function slugify(value) {
    if (!value) return '';
    return value
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  function getProductWrapper(container) {
    return container.closest('.product') || container.parentElement;
  }

  function nameIsColorOption(name) {
    if (!name) return false;
    const trimmedLower = name.trim().toLowerCase();
    return trimmedLower.indexOf('color') === 0 || trimmedLower.indexOf('colour') === 0;
  }

  function getCurrentColorSlug(productWrapper) {
    if (!productWrapper) return null;

    const checkedInput = Array.prototype.find.call(
      productWrapper.querySelectorAll('input[type="radio"]:checked'),
      (el) => nameIsColorOption(el.name)
    );
    if (checkedInput) return slugify(checkedInput.value);

    const select = Array.prototype.find.call(
      productWrapper.querySelectorAll('select'),
      (el) => nameIsColorOption(el.name)
    );
    if (select) return slugify(select.value);

    return null;
  }

  function updateCounter(mediaGallery) {
    const visibleSlides = mediaGallery.querySelectorAll(
      '.product__media-item[data-variant-color]:not(.nauma-media-hidden)'
    );
    const counterTotal = mediaGallery.querySelector('.slider-counter--total');
    if (counterTotal) counterTotal.textContent = visibleSlides.length;
  }

  function ensureActiveSlideVisible(mediaGallery) {
    const sliderComponent = mediaGallery.querySelector('slider-component');
    if (!sliderComponent) return;

    const activeSlide = sliderComponent.querySelector('.is-active');
    const activeHidden = activeSlide && activeSlide.classList.contains('nauma-media-hidden');

    if (!activeSlide || activeHidden) {
      const firstVisible = sliderComponent.querySelector(
        '.product__media-item:not(.nauma-media-hidden)'
      );
      if (firstVisible) {
        sliderComponent.querySelectorAll('.product__media-item.is-active').forEach((el) => {
          el.classList.remove('is-active');
        });
        firstVisible.classList.add('is-active');
        firstVisible.scrollIntoView({ block: 'nearest', inline: 'start' });
      }
    }
  }

  // Works on any container with [data-variant-color] children — the main
  // gallery (<media-gallery>) or the zoom modal (<product-modal>).
  function filterContainer(container, colorSlug) {
    if (!colorSlug) return;

    const items = container.querySelectorAll('[data-variant-color]');
    items.forEach((item) => {
      const itemColor = item.getAttribute('data-variant-color');
      const visible = itemColor === colorSlug || itemColor === 'all';
      item.classList.toggle('nauma-media-hidden', !visible);
    });

    if (container.matches('media-gallery')) {
      updateCounter(container);
      ensureActiveSlideVisible(container);
    }
  }

  function initContainer(container) {
    const productWrapper = getProductWrapper(container);
    const colorSlug = getCurrentColorSlug(productWrapper);
    if (colorSlug) filterContainer(container, colorSlug);
  }

  function allContainers() {
    return document.querySelectorAll('media-gallery, product-modal');
  }

  function handleVariantChange(event) {
    const sectionId = event?.detail?.data?.sectionId;
    if (!sectionId) return;

    const mediaGallery = document.getElementById('MediaGallery-' + sectionId);
    const modal = document.getElementById('ProductModal-' + sectionId);
    const targets = [mediaGallery, modal].filter(Boolean);
    if (!targets.length) return;

    const variant = event.detail.data.variant;
    if (!variant || !variant.options) return;

    targets.forEach((container) => {
      const knownColors = new Set();
      container.querySelectorAll('[data-variant-color]').forEach((el) => {
        const c = el.getAttribute('data-variant-color');
        if (c && c !== 'all') knownColors.add(c);
      });

      const matchedOption = variant.options.find((optionValue) => knownColors.has(slugify(optionValue)));
      if (matchedOption) {
        filterContainer(container, slugify(matchedOption));
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    allContainers().forEach(initContainer);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
    }

    // Fallback: react directly to colour swatch clicks, independent of
    // whether Dawn finds a fully matching variant (handles out-of-stock /
    // missing variant combinations gracefully). One listener per product
    // wrapper, filtering every gallery/modal container found within it.
    const seenWrappers = new Set();
    allContainers().forEach((container) => {
      const productWrapper = getProductWrapper(container);
      if (!productWrapper || seenWrappers.has(productWrapper)) return;
      seenWrappers.add(productWrapper);

      productWrapper.addEventListener('change', function (event) {
        const target = event.target;
        if (!target || !nameIsColorOption(target.name)) return;

        const colorSlug = slugify(target.value);
        if (!colorSlug) return;

        productWrapper.querySelectorAll('media-gallery, product-modal').forEach((c) => {
          filterContainer(c, colorSlug);
        });
      });
    });
  });
})();
